import { Injectable } from '@angular/core';
import { SurveyQuestion, SurveyResponse } from '@app/schemas/surveys';

@Injectable({
  providedIn: 'root'
})
export class SurveyStorageService {
  private readonly STORAGE_KEY = 'survey_responses';
  private readonly USER_RESPONSES_KEY = 'survey_user_responses';

  saveSurveyQuestions(questions: SurveyQuestion[]): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(questions));
      }
    } catch (error) {
      console.warn('Failed to save survey responses to localStorage:', error);
    }
  }

  loadSurveyQuestions(): SurveyQuestion[] {
    try {
      if (typeof localStorage !== 'undefined') {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
      }
      return [];
    } catch (error) {
      console.warn('Failed to load survey responses from localStorage:', error);
      return [];
    }
  }

  clearSurveyQuestions(): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(this.STORAGE_KEY);
      }
    } catch (error) {
      console.warn('Failed to clear survey responses from localStorage:', error);
    }
  }

  saveUserResponses(responses: SurveyResponse[]): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(this.USER_RESPONSES_KEY, JSON.stringify(responses));
      }
    } catch (error) {
      console.warn('Failed to save user responses to localStorage:', error);
    }
  }

  loadUserResponses(): SurveyResponse[] {
    try {
      if (typeof localStorage !== 'undefined') {
        const stored = localStorage.getItem(this.USER_RESPONSES_KEY);
        return stored ? JSON.parse(stored) : [];
      }
      return [];
    } catch (error) {
      console.warn('Failed to load user responses from localStorage:', error);
      return [];
    }
  }

  clearUserResponses(): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(this.USER_RESPONSES_KEY);
      }
    } catch (error) {
      console.warn('Failed to clear user responses from localStorage:', error);
    }
  }

  saveCompletedSteps(steps: number[]): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('survey_completed_steps', JSON.stringify(steps));
      }
    } catch (error) {
      console.warn('Failed to save completed steps to localStorage:', error);
    }
  }

  loadCompletedSteps(): number[] {
    try {
      if (typeof localStorage !== 'undefined') {
        const stored = localStorage.getItem('survey_completed_steps');
        return stored ? JSON.parse(stored) : [];
      }
      return [];
    } catch (error) {
      console.warn('Failed to load completed steps from localStorage:', error);
      return [];
    }
  }

  clearCompletedSteps(): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('survey_completed_steps');
      }
    } catch (error) {
      console.warn('Failed to clear completed steps from localStorage:', error);
    }
  }

  setSurveyStarted(): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('survey_started', 'true');
      }
    } catch (error) {
      console.warn('Failed to set survey started flag in localStorage:', error);
    }
  }

  isSurveyStarted(): boolean {
    try {
      if (typeof localStorage !== 'undefined') {
        return localStorage.getItem('survey_started') === 'true';
      }
      return false;
    } catch (error) {
      console.warn('Failed to read survey started flag from localStorage:', error);
      return false;
    }
  }

  clearSurveyStarted(): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('survey_started');
      }
    } catch (error) {
      console.warn('Failed to clear survey started flag from localStorage:', error);
    }
  }
}